// -----------------------------------------------------------------------------
// ChecklistMiniGrid
//
// Compact horizontal layout for the area under the Up Next card. Shows up to
// 2 active items as side-by-side mini cards plus a "+N more" button to expand.
// Expanded state wraps all active items in a 2-column responsive grid.
//
// Also renders done items (completed / skipped / paused) as outcome chips with
// an Undo button (Task #15).
//
// Designed to reclaim vertical space lost by the stacked ChecklistList variant
// — the Up Next + next-stage UI stays visible without scrolling.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import moment from 'moment';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { ACTION_META, DEFAULT_ACTION_META } from './actionMeta';

// Outcome chip styles per completion state
const COMPLETION_CHIP = {
    YES:     { label: 'Done',    bg: 'rgba(52,199,89,0.10)',   color: '#34c759' },
    SKIPPED: { label: 'Skipped', bg: 'rgba(110,110,115,0.10)', color: '#6e6e73' },
    PAUSED:  { label: 'Paused',  bg: 'rgba(255,171,0,0.10)',   color: '#FFAB00' },
};

const DONE_COLLAPSED_LIMIT = 2;

const COLLAPSED_LIMIT = 2;

function formatDueShort(dueDateStr) {
    if (!dueDateStr) return null;
    const now = moment();
    const due = moment(dueDateStr);
    if (due.isBefore(now)) {
        const days = now.diff(due, 'days');
        if (days >= 1) return `Overdue ${days}d`;
        const hours = now.diff(due, 'hours');
        if (hours >= 1) return `Overdue ${hours}h`;
        return `Overdue ${now.diff(due, 'minutes')}m`;
    }
    if (due.isSame(now, 'day')) return `Today ${due.format('HH:mm')}`;
    return due.format('D MMM');
}

// Default datetime-local string for the change-due-date picker: tomorrow 09:00.
function defaultDueLocal() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}

function MiniCard({ item, onClick, onSkip, onChangeDueDate }) {
    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const isOverdue = item.due_at && new Date(item.due_at) < new Date();
    const title = item.title || item.name || meta.label;
    const dueText = formatDueShort(item.due_at);
    const chipBg = isOverdue ? 'rgba(255,86,48,0.10)' : 'rgba(255,171,0,0.10)';
    const chipColor = isOverdue ? '#FF5630' : '#FFAB00';

    // Overflow (⋯) menu is ADDITIVE: it only renders when at least one of the
    // per-item Skip / Change-due-date handlers is supplied. The attended popup
    // passes none → this whole block is inert and the card renders as before.
    // NOTE: Pause is intentionally NOT a per-item action here — the /pause/
    // endpoint pauses the whole task-group, so the hub surfaces it as a single
    // checklist-level "Pause checklist" control instead.
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [dueAnchor, setDueAnchor] = useState(null);
    const [dueValue, setDueValue] = useState('');
    const hasOverflow = !!(onSkip || onChangeDueDate);

    const openMenu = (e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
    const closeMenu = () => setMenuAnchor(null);

    const handleSkipClick = (e) => { e.stopPropagation(); closeMenu(); onSkip?.(item); };
    const handleDueOpen = (e) => {
        e.stopPropagation();
        setDueValue(item.due_at ? moment(item.due_at).format('YYYY-MM-DDTHH:mm') : defaultDueLocal());
        setDueAnchor(e.currentTarget);
        closeMenu();
    };
    const handleDueConfirm = (e) => {
        e.stopPropagation();
        if (dueValue && onChangeDueDate) onChangeDueDate(item, new Date(dueValue));
        setDueAnchor(null);
    };

    return (
        <Box
            data-testid={`mini-card-${item.id ?? item.checklist_item_id}`}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            sx={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                bgcolor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'background 150ms, box-shadow 160ms, transform 160ms',
                '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.02)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    transform: 'translateY(-1px)',
                },
            }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: `${meta.color}14`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon={meta.icon} width={14} height={14} sx={{ color: meta.color }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#1d1d1f',
                        letterSpacing: '-0.005em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                {dueText && (
                    <Typography
                        sx={{
                            display: 'inline-block',
                            mt: 0.25,
                            px: 0.75,
                            py: 0.1,
                            borderRadius: '980px',
                            bgcolor: chipBg,
                            color: chipColor,
                            fontSize: '10px',
                            fontWeight: 500,
                            letterSpacing: '-0.005em',
                        }}
                    >
                        {dueText}
                    </Typography>
                )}
            </Box>

            {hasOverflow && (
                <>
                    <IconButton
                        data-testid={`mini-card-overflow-${item.id ?? item.checklist_item_id}`}
                        size="small"
                        aria-label="More actions"
                        onClick={openMenu}
                        sx={{ p: 0.25, ml: 0.5, flexShrink: 0, color: '#6e6e73', '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' } }}
                    >
                        <Iconify icon="eva:more-horizontal-fill" width={16} height={16} />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={(e) => { e?.stopPropagation?.(); closeMenu(); }}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {onSkip && (
                            <MenuItem data-testid="mini-card-skip" onClick={handleSkipClick} sx={{ fontSize: '13px' }}>
                                <ListItemIcon sx={{ minWidth: 28 }}><Iconify icon="eva:skip-forward-outline" width={16} height={16} /></ListItemIcon>
                                Skip
                            </MenuItem>
                        )}
                        {onChangeDueDate && (
                            <MenuItem data-testid="mini-card-change-due" onClick={handleDueOpen} sx={{ fontSize: '13px' }}>
                                <ListItemIcon sx={{ minWidth: 28 }}><Iconify icon="eva:calendar-outline" width={16} height={16} /></ListItemIcon>
                                Change due date
                            </MenuItem>
                        )}
                    </Menu>

                    {/* Change-due-date popover — datetime-local + confirm */}
                    <Menu
                        anchorEl={dueAnchor}
                        open={Boolean(dueAnchor)}
                        onClose={(e) => { e?.stopPropagation?.(); setDueAnchor(null); }}
                        onClick={(e) => e.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { p: 1.5, borderRadius: '12px' } }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43' }}>New due date</Typography>
                            <input
                                data-testid="mini-card-due-input"
                                type="datetime-local"
                                value={dueValue}
                                onChange={(e) => setDueValue(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: '1.5px solid rgba(0,0,0,0.15)',
                                    fontFamily: 'inherit',
                                    color: '#1d1d1f',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <Box
                                component="button"
                                data-testid="mini-card-due-confirm"
                                disabled={!dueValue}
                                onClick={handleDueConfirm}
                                sx={{
                                    border: 'none',
                                    borderRadius: '8px',
                                    bgcolor: '#078DEE',
                                    color: '#fff',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    py: 0.75,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    '&:disabled': { bgcolor: 'rgba(7,141,238,0.4)', cursor: 'default' },
                                }}
                            >
                                Update due date
                            </Box>
                        </Box>
                    </Menu>
                </>
            )}
        </Box>
    );
}
MiniCard.propTypes = {
    item: PropTypes.object.isRequired,
    onClick: PropTypes.func,
    /** Additive — when supplied, the overflow (⋯) menu exposes Skip. */
    onSkip: PropTypes.func,
    /** Additive — when supplied, the overflow (⋯) menu exposes Change due date. */
    onChangeDueDate: PropTypes.func,
};
MiniCard.defaultProps = { onClick: null, onSkip: null, onChangeDueDate: null };

// ---------------------------------------------------------------------------
// DoneCard — completed / skipped / paused item with outcome chip + Undo
// ---------------------------------------------------------------------------

function DoneCard({ item, onUndo }) {
    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const title = item.title || item.name || meta.label;
    // Resolve completion state: prefer the item's own field, fall back to 'YES'
    // only when the item is genuinely done with no explicit state (legacy items).
    // Do NOT default SKIPPED/PAUSED items to 'YES' — that mislabels the chip.
    const rawState = item.completion_state || null;
    const state = (rawState && COMPLETION_CHIP[rawState]) ? rawState : 'YES';
    const chip = COMPLETION_CHIP[state];

    // For PAUSED items, append the date the item is paused until (if available).
    // paused_until comes back as the app's "DD-MM-YYYY HH:mm:ss.SSSSSS[Z]" format
    // (probed: e.g. "04-06-2026 10:00:00.000000Z" = 4 June). A bare moment(str)
    // mis-parses this as M-D-Y → "6 Apr". Parse explicitly with the known formats
    // (matches src/utils/multiTreatmentFlow.js), falling back to ISO_8601.
    const pausedUntil = state === 'PAUSED' ? (item.paused_until || null) : null;
    const pausedMoment = pausedUntil
        ? moment(pausedUntil, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss.SSSSSS[Z]', 'DD-MM-YYYY HH:mm:ss', 'DD-MM-YYYY', moment.ISO_8601])
        : null;
    const pausedUntilLabel = pausedMoment && pausedMoment.isValid()
        ? ` until ${pausedMoment.format('D MMM')}`
        : '';
    const chipLabel = `${chip.label}${pausedUntilLabel}`;

    return (
        <Box
            data-testid={`done-card-${item.id ?? item.checklist_item_id}`}
            sx={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                bgcolor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '10px',
                opacity: 0.8,
            }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: `${meta.color}0A`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon={meta.icon} width={14} height={14} sx={{ color: meta.color, opacity: 0.5 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6e6e73',
                        letterSpacing: '-0.005em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                        textDecoration: 'line-through',
                    }}
                >
                    {title}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        mt: 0.25,
                        px: 0.75,
                        py: 0.1,
                        borderRadius: '980px',
                        bgcolor: chip.bg,
                        color: chip.color,
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '-0.005em',
                    }}
                >
                    {chipLabel}
                </Box>
            </Box>
            {onUndo && (
                <Box
                    component="button"
                    data-testid={`undo-btn-${item.id ?? item.checklist_item_id}`}
                    onClick={(e) => { e.stopPropagation(); onUndo(item); }}
                    sx={{
                        background: 'none',
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        px: 0.875,
                        py: 0.3,
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#6e6e73',
                        letterSpacing: '-0.005em',
                        fontFamily: 'inherit',
                        flexShrink: 0,
                        transition: 'background 150ms, color 150ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' },
                    }}
                >
                    Undo
                </Box>
            )}
        </Box>
    );
}
DoneCard.propTypes = {
    item: PropTypes.object.isRequired,
    onUndo: PropTypes.func,
};
DoneCard.defaultProps = { onUndo: null };

function MoreButton({ count, onClick, expanded }) {
    return (
        <Box
            data-testid="checklist-mini-more-btn"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            sx={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.5,
                py: 1,
                bgcolor: 'rgba(7,141,238,0.06)',
                border: '1px solid rgba(7,141,238,0.20)',
                borderRadius: '10px',
                cursor: 'pointer',
                color: '#078DEE',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '-0.005em',
                whiteSpace: 'nowrap',
                transition: 'background 150ms',
                '&:hover': { bgcolor: 'rgba(7,141,238,0.10)' },
            }}
        >
            <Iconify
                icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                width={14}
                height={14}
            />
            {expanded ? 'Show less' : `+${count} more`}
        </Box>
    );
}
MoreButton.propTypes = {
    count: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
    expanded: PropTypes.bool,
};
MoreButton.defaultProps = { expanded: false };

// Above this many visible cards, cap the section height and let it scroll so a
// long checklist (e.g. the wrap-up hub with ~12 items) doesn't push the page.
const SCROLL_CARD_THRESHOLD = 6;
// Responsive auto-fill grid: cards never shrink below ~210px, so two items sit
// side-by-side (as before) and many items wrap onto multiple rows instead of
// cramming into one tight row. Safe for both the hub (many) and attended (few).
const GRID_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: 1,
    alignItems: 'stretch',
};

export default function ChecklistMiniGrid({ groups, onItemClick, onUndo, onSkip, onChangeDueDate }) {
    const [expanded, setExpanded] = useState(false);
    const [doneExpanded, setDoneExpanded] = useState(false);
    const { today = [], later = [], done = [] } = groups || {};
    // Upcoming cards must always read in due-date order (earliest first) and
    // must re-order live after a reschedule. today items (≤ end-of-today) sort
    // ahead of later items (future); undated items (due_at null → Infinity) last.
    const byDueAsc = (a, b) =>
        (a.due_at ? new Date(a.due_at).getTime() : Infinity) -
        (b.due_at ? new Date(b.due_at).getTime() : Infinity);
    const active = [...today, ...later].sort(byDueAsc);
    if (active.length === 0 && done.length === 0) return null;

    const visible = expanded ? active : active.slice(0, COLLAPSED_LIMIT);
    const hiddenCount = active.length - COLLAPSED_LIMIT;

    const visibleDone = doneExpanded ? done : done.slice(0, DONE_COLLAPSED_LIMIT);
    const hiddenDoneCount = done.length - DONE_COLLAPSED_LIMIT;

    // Cap height + scroll only once enough cards are showing to risk pushing the
    // page; keeps small lists (attended) at natural height with no scrollbar.
    const activeScrolls = visible.length > SCROLL_CARD_THRESHOLD;
    const doneScrolls = visibleDone.length > SCROLL_CARD_THRESHOLD;

    return (
        <Box data-testid="checklist-mini-grid">
            {/* Active items — responsive wrapping grid */}
            {active.length > 0 && (
                <Box
                    sx={{
                        ...GRID_SX,
                        ...(activeScrolls
                            ? { maxHeight: 320, overflowY: 'auto', pr: 0.5 }
                            : {}),
                    }}
                >
                    {visible.map((item) => (
                        <MiniCard
                            key={item.id ?? item.checklist_item_id ?? item.sort_order}
                            item={item}
                            onClick={() => onItemClick?.(item)}
                            onSkip={onSkip}
                            onChangeDueDate={onChangeDueDate}
                        />
                    ))}
                    {hiddenCount > 0 && (
                        <MoreButton
                            count={hiddenCount}
                            expanded={expanded}
                            onClick={() => setExpanded((v) => !v)}
                        />
                    )}
                </Box>
            )}

            {/* Done items — outcome chips with Undo */}
            {done.length > 0 && (
                <Box
                    sx={{
                        mt: active.length > 0 ? 1.25 : 0,
                        ...GRID_SX,
                        ...(doneScrolls
                            ? { maxHeight: 320, overflowY: 'auto', pr: 0.5 }
                            : {}),
                    }}
                >
                    {visibleDone.map((item) => (
                        <DoneCard
                            key={item.id ?? item.checklist_item_id ?? item.sort_order}
                            item={item}
                            onUndo={onUndo}
                        />
                    ))}
                    {hiddenDoneCount > 0 && (
                        <MoreButton
                            count={hiddenDoneCount}
                            expanded={doneExpanded}
                            onClick={() => setDoneExpanded((v) => !v)}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}

ChecklistMiniGrid.propTypes = {
    groups: PropTypes.shape({
        today: PropTypes.array,
        later: PropTypes.array,
        done: PropTypes.array,
    }),
    onItemClick: PropTypes.func,
    /** Called with the done item when user clicks Undo → reopens the checklist item. */
    onUndo: PropTypes.func,
    /** Additive — per-item Skip (overflow menu). Attended popup passes none. */
    onSkip: PropTypes.func,
    /** Additive — per-item Change due date (overflow menu). */
    onChangeDueDate: PropTypes.func,
};

ChecklistMiniGrid.defaultProps = {
    groups: { today: [], later: [], done: [] },
    onItemClick: null,
    onUndo: null,
    onSkip: null,
    onChangeDueDate: null,
};
